<script setup lang="ts">
/**
 * Hardcoded woningstatistieken voor de homepage.
 *
 * Ververs deze data met de volgende SQL query op Supabase:
 *
 * SELECT
 *   COUNT(*) FILTER (WHERE status = 'sold') AS total_sold,
 *   COUNT(*) FILTER (WHERE status = 'sold' AND sale_date >= CURRENT_DATE - INTERVAL '90 days') AS sold_last_90d,
 *   COUNT(*) FILTER (WHERE status = 'available') AS currently_available,
 *   ROUND(AVG(price) FILTER (WHERE status = 'sold' AND sale_date >= CURRENT_DATE - INTERVAL '90 days')) AS avg_price_sold_90d,
 *   ROUND(AVG(sale_date - publication_date::date) FILTER (WHERE status = 'sold' AND sale_date >= CURRENT_DATE - INTERVAL '90 days' AND publication_date IS NOT NULL)) AS avg_days_on_market_90d
 * FROM funda_listings;
 *
 * Laatste update: 2026-03-19
 */

const content = {
  title: 'Onze data op een rij',
  subtitle: 'De woningmarkt volgens onze database',
}

const barChartData = [35, 55, 40, 65, 50, 75, 60, 80, 70, 90, 75, 85]
</script>

<template>
  <section class="py-16 bg-muted">
    <UContainer>
      <div class="mb-10">
        <h2 class="text-3xl sm:text-4xl font-bold tracking-[-0.015em] text-highlighted text-balance mb-2">
          {{ content.title }}
        </h2>
        <p class="text-muted text-lg text-pretty">
          {{ content.subtitle }}
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <!-- Rij 1: twee grote kaarten -->
        <dl class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <!-- Geanalyseerde transacties — hero card, dark -->
          <div class="rounded-xl border border-default bg-default p-8 flex flex-col justify-between min-h-[260px]">
            <div>
              <dt class="text-sm font-medium text-muted mb-2">
                Geanalyseerde transacties
              </dt>
              <dd class="text-5xl sm:text-6xl font-bold tracking-[-0.02em] text-highlighted tabular-nums">
                373.917
              </dd>
            </div>
            <div class="flex items-end gap-1.5 h-20 mt-8">
              <div
                v-for="(height, i) in barChartData"
                :key="i"
                class="flex-1 rounded-sm"
                :class="i >= 10 ? 'bg-primary' : 'bg-neutral-200'"
                :style="{ height: `${height}%` }"
              />
            </div>
            <p class="text-xs text-dimmed mt-2">
              Transactievolume per maand
            </p>
          </div>

          <!-- Gemiddelde verkooprijs -->
          <div class="rounded-xl border border-default bg-default p-8 flex flex-col justify-between min-h-[260px]">
            <div>
              <dt class="text-sm font-medium text-muted mb-2">
                Gem. verkooprijs
              </dt>
              <dd class="text-5xl sm:text-6xl font-bold tracking-[-0.02em] text-highlighted tabular-nums">
                € 435k
              </dd>
            </div>
            <div class="mt-8">
              <div class="flex gap-0.5 h-4">
                <div
                  class="rounded-sm bg-primary"
                  style="flex: 43.5"
                />
                <div
                  class="rounded-sm bg-neutral-200"
                  style="flex: 56.5"
                />
              </div>
              <div class="flex justify-between text-xs text-dimmed mt-2">
                <span>€ 0</span>
                <span class="text-primary font-semibold">▲ € 435k mediaan</span>
                <span>€ 1.000.000</span>
              </div>
            </div>
          </div>
        </dl>

        <!-- Rij 2: drie kleinere kaarten -->
        <dl class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Verkocht afgelopen 90 dagen -->
          <div class="rounded-xl border border-default bg-default p-6 flex flex-col">
            <dt class="text-sm font-medium text-muted mb-1">
              Verkocht
            </dt>
            <dd class="text-4xl font-bold tracking-[-0.02em] text-highlighted tabular-nums">
              3.960
            </dd>
            <p class="text-xs text-dimmed mt-1">
              afgelopen 90 dagen
            </p>
            <div class="mt-auto pt-5">
              <div class="flex gap-1">
                <div
                  v-for="i in 20"
                  :key="i"
                  class="flex-1 h-4 rounded-sm"
                  :class="i <= 14 ? 'bg-neutral-200' : 'bg-success'"
                />
              </div>
              <div class="flex justify-between text-xs text-dimmed mt-2">
                <span>Eerder</span>
                <span>Recent</span>
              </div>
            </div>
          </div>

          <!-- Nu beschikbaar -->
          <div class="rounded-xl border border-default bg-default p-6 flex flex-col">
            <dt class="text-sm font-medium text-muted mb-1">
              Nu beschikbaar
            </dt>
            <dd class="text-4xl font-bold tracking-[-0.02em] text-highlighted tabular-nums">
              28.684
            </dd>
            <p class="text-xs text-dimmed mt-1">
              woningen op de markt
            </p>
            <div class="mt-auto pt-5">
              <div class="flex gap-0.5 h-4">
                <div class="flex-[3] rounded-sm bg-primary" />
                <div class="flex-[2] rounded-sm bg-orange-500" />
                <div class="flex-[1] rounded-sm bg-neutral-300" />
              </div>
              <div class="flex justify-between text-xs text-dimmed mt-2">
                <span>Appartementen</span>
                <span>Huizen</span>
              </div>
            </div>
          </div>

          <!-- Gemiddelde verkooptijd -->
          <div class="rounded-xl border border-default bg-default p-6 flex flex-col">
            <dt class="text-sm font-medium text-muted mb-1">
              Gem. verkooptijd
            </dt>
            <dd class="text-4xl font-bold tracking-[-0.02em] text-highlighted tabular-nums">
              52 <span class="text-lg font-normal text-muted">dagen</span>
            </dd>
            <p class="text-xs text-dimmed mt-1 invisible">
              &nbsp;
            </p>
            <div class="mt-auto pt-5">
              <div class="flex gap-0.5 h-4">
                <div
                  class="rounded-sm bg-orange-500"
                  style="flex: 52"
                />
                <div
                  class="rounded-sm bg-neutral-200"
                  style="flex: 38"
                />
              </div>
              <div class="flex justify-between text-xs text-dimmed mt-2">
                <span>Dag 1</span>
                <span class="text-orange-600 font-semibold">52</span>
                <span>90</span>
              </div>
            </div>
          </div>
        </dl>
      </div>
    </UContainer>
  </section>
</template>
